import { IRegionShort, IWebcamItem } from '@/api/region/types';
import { IPaginationResponse, IResortPathInfo } from '@/api/resort/types';
import { IPaginableResponse } from '@/types';

export enum ResortTopRated {
  overall = 'overall',
  allMtnTerrain = 'allMtnTerrain',
  familyFriendly = 'familyFriendly',
  terrainPark = 'terrainPark',
  beginner = 'beginner',
  apresSki = 'apresSki',
  smallSkiArea = 'smallSkiArea',
  intermediate = 'intermediate',
  expert = 'expert',
}

export enum SearchCategory {
  resorts = 'resorts',
  regions = 'regions',
  other = 'other',
  news = 'news',
}

export type ISearchResult = {
  categories: Array<IResultCategory>;
  results: {
    other: Array<ISearchResultOther>,
    resorts: Array<ISearchResultResort>,
    regions: Array<ISearchResultRegion>,
  }
  pagination: IPaginationResponse;
};

export type ISearchNewsResult = {
  results: Array<ISearchResultNews>;
};

export type IResultCategory = {
  uuid: SearchCategory;
  title: string;
};

export interface ISearchResultCommon {
  uuid: string;
  title: string;
  /** Relevance score from the search index (MiniSearch BM25 with our
   *  boosts applied). Higher is more relevant. Optional so legacy
   *  call paths that build results manually don't have to populate
   *  it. Consumers that interleave categories should sort by this. */
  score?: number;
}

export interface ISearchResultResort extends ISearchResultCommon {
  categoryUuid: SearchCategory.resorts;
  resort: IResortPathInfo;
  regionTitle?: string;
}

export interface ISearchResultOther extends ISearchResultCommon {
  categoryUuid: SearchCategory.other;
  href: string;
}

export interface ISearchResultRegion extends ISearchResultCommon {
  categoryUuid: SearchCategory.regions;
  region: IRegionShort;
}

export interface ISearchResultNews extends ISearchResultCommon {
  categoryUuid: SearchCategory.news;
  news: INews;
}

export type ISearchResultItem = ISearchResultResort | ISearchResultRegion | ISearchResultNews | ISearchResultOther;

export type IPopularSearch = {
  results: Array<string | IRegionShort>;
};

export type IBackgroundImages = {
  image: string;
};

export type ISnowWebcam = {
  webcam: IWebcamItem;
  resort: IResortPathInfo;
  regionName?: string;
};

export type IResortTopRated = {
  resort: IResort;
  resortPath: IResortPathInfo;
};

export type IResort = {
  uuid: string;
  title: string;
  image: string;
  smallImage: string;
  rating: number;
  reviewsCount: number;
};

export type ISocialMediaAccount = {
  type: string;
  handle: string;
};

export interface IFeaturedResort {
  uuid: string;
  title: string;
  region: IRegionShort;
  image: string;
  smallImage: string;
  video: string | null;
  website: string | null;
  phone: string | null;
  isPartner: boolean;
  twitter: string | null;
  liftsUrl: string | null;
  rentalUrl: string | null;
  lessonsUrl: string | null;
  socialMediaAccounts: Array<ISocialMediaAccount>;
  description?: string | null;
}

export type INews = {
  uuid: string;
  title: string;
  description: string;
  image?: string;
  thumbnail?: string;
  categories?: Array<ICategoryNews>;
  slug?: string;
  link?: string;
};

export interface ICategoryNews extends INews {
  slug: string;
}

export type ILastNews = {
  uuid: string;
  title: string;
  description: string;
  image: string;
};

export type INewsFromCategory = {
  pagination: IPaginationResponse;
  newsList: Array<INews>;
};

export type IGuideHomeTopRated = IPaginableResponse<IResortTopRated>;

export type IFirstHandReport = {
  uuid: string;
  title: string;
  image: string;
  largeImage: string;
  name: string | null;
  viewCount: number;
  date: string;
  resortUuid: number;
  body: string;
  translated: string;
};

export type IFirstHandReportsResponse = {
  pagination: IPaginationResponse;
  reportsList: Array<IFirstHandReport>;
};

export type IMostReportedSnowfallSnow = {
  last24: number | null;
};

export interface IMostReportedSnowfallItem {
  uuid: string;
  title: string;
  title_short: string;
  title_original: string;
  slug: string;
  region: string;
  region_id: string;
  region_en: string;
  country_code: string;
  domain: string | null;
  surfaceType: number;
  snow: IMostReportedSnowfallSnow;
  updatedAt: string;
}

export interface IMostReportedSnowfallResponse {
  pagination: IPaginationResponse;
  data: Array<IMostReportedSnowfallItem>;
}
